import { hasPermission } from "@dashboard/auth/misc";
import { useUser } from "@dashboard/auth/useUser";
import { PermissionEnum, useSaveOnBoardingStateMutation } from "@dashboard/graphql";
import debounce from "lodash/debounce";
import { useCallback, useMemo } from "react";

import { OnboardingState, StorageService } from "../onboardingContext/types";
import { METADATA_KEY, prepareUserMetadata } from "./utils";

export const useOnboardingStorage = (): StorageService => {
  const { authenticated, user } = useUser();
  const [saveOnboarding] = useSaveOnBoardingStateMutation({
    disableErrorHandling: true,
  });

  const getOnboardingState: StorageService["getOnboardingState"] = () => {
    try {
      const metadata = user?.metadata?.find(m => m.key === METADATA_KEY);

      if (!metadata) {
        return undefined;
      }

      return JSON.parse(metadata.value);
    } catch (error) {
      console.warn("Could not get onboarding state from metadata", { error });

      return undefined;
    }
  };

  const saveOnboardingState: StorageService["saveOnboardingState"] = useCallback(
    async (onboardingState: OnboardingState) => {
      const canManageStaff = !!user && hasPermission(PermissionEnum.MANAGE_STAFF, user);

      if (!authenticated || !user || !canManageStaff) {
        return;
      }

      try {
        const userMetadata = prepareUserMetadata(user?.metadata, onboardingState);

        await saveOnboarding({
          variables: {
            id: user.id,
            input: userMetadata,
          },
        });
      } catch (error) {
        console.warn("Could not save onboarding state to metadata");
      }
    },
    [authenticated, saveOnboarding, user],
  );

  const debouncedSaveOnboardingState = useMemo(
    () => debounce(saveOnboardingState, 1000),
    [saveOnboardingState],
  ) as StorageService["saveOnboardingState"];

  return {
    getOnboardingState,
    saveOnboardingState: debouncedSaveOnboardingState,
  };
};
